// BuildiumLeases.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  FiRefreshCw,
  FiSearch,
  FiX,
  FiFileText,
  FiFilter,
  FiInfo,
} from "react-icons/fi";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { FiDownload } from "react-icons/fi";
import ExportRenewalModal from "./ExportRenewalModal";
import RenewalModal from "./RenewalModal";

const API_BASE = "https://rentdoc-a0d223e1499e.herokuapp.com/api";
const SYNC_COOLDOWN_MS = 5 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 350;
const ONTARIO_GUIDELINE_LIMIT = 1.9;
const AlertDialog = ({
  type = "error",
  title = "Alert",
  message = "Something happened.",
  confirmText = "OK",
  cancelText = null,
  open = false,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const styles = {
    error: {
      icon: <ExclamationTriangleIcon className="size-6 text-red-400" />,
      iconBg: "bg-red-100",
      buttonBg: "bg-red-600 hover:bg-red-700",
    },
    success: {
      icon: <CheckCircleIcon className="size-6 text-green-600" />,
      iconBg: "bg-green-100",
      buttonBg: "bg-green-600 hover:bg-green-700",
    },
  };

  const { icon, iconBg, buttonBg } = styles[type] || styles.error;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0 sm:h-10 sm:w-10`}
              >
                {icon}
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{message}</p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                className={`inline-flex w-full justify-center rounded-md ${buttonBg} px-3 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto`}
              >
                {confirmText}
              </button>
              {cancelText && (
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  {cancelText}
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

const LeaseInfoPopover = ({ details, isBlocked }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  const POPOVER_WIDTH = 288; // matches w-72
  const VIEWPORT_MARGIN = 12;

  const computeCoords = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();

    // Anchor the popover's right edge to the button's right edge by default,
    // then clamp so it never runs off either side of the viewport.
    let left = rect.right - POPOVER_WIDTH;
    left = Math.max(VIEWPORT_MARGIN, left);
    left = Math.min(left, window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN);

    setCoords({ top: rect.bottom + 8, left });
  };

  const toggleOpen = () => {
    if (!open) computeCoords();
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const handleReposition = () => computeCoords();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        title="Lease renewal details"
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full border transition ${
          isBlocked
            ? "border-amber-300 text-amber-600 bg-amber-50 hover:bg-amber-100"
            : "border-gray-300 text-gray-400 bg-white hover:bg-gray-50 hover:text-gray-600"
        }`}
      >
        <FiInfo className="w-3.5 h-3.5" />
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              width: POPOVER_WIDTH,
            }}
            className="z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
          >
            <div className="text-xs font-semibold text-gray-700 mb-2">
              Lease Details
            </div>
            <ul className="space-y-1.5">
              {details.map((line, idx) => (
                <li
                  key={idx}
                  className="text-xs text-gray-600 flex items-start gap-1.5"
                >
                  <span className="mt-1 w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
};

const EXPIRY_OPTIONS = [
  { label: "241+ days", value: "241+" },
  { label: "181-240 days", value: "181-240" },
  { label: "121-180 days", value: "121-180" },
  { label: "91-120 days", value: "91-120" },
  { label: "61-90 days", value: "61-90" },
  { label: "31-60 days", value: "31-60" },
  { label: "30 days or fewer", value: "0-30" },
  { label: "Expired", value: "expired" },
];

const LEASE_TYPE_OPTIONS = [
  { label: "All Lease Types", value: "" },
  { label: "Fixed w/ Rollover", value: "FixedWithRollover" },
  { label: "Month-to-Month", value: "MonthToMonth" },
];

// Lease types we deliberately do not track/maintain in this system.
const EXCLUDED_LEASE_TYPES = ["Fixed"];

function daysBetweenDateStrings(fromStr, toStr) {
  if (!fromStr || !toStr) return null;
  const [fy, fm, fd] = fromStr.split("T")[0].split("-").map(Number);
  const [ty, tm, td] = toStr.split("T")[0].split("-").map(Number);
  const from = new Date(fy, fm - 1, fd);
  const to = new Date(ty, tm - 1, td);
  return Math.round((to - from) / (1000 * 60 * 60 * 24));
}

function todayDateString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

const calculateDaysLeft = (leaseToDate) => {
  if (!leaseToDate) return null;
  return daysBetweenDateStrings(todayDateString(), leaseToDate.split("T")[0]);
};

function getMovingOutTenants(lease) {
  if (!lease.moveOutData || lease.moveOutData.length === 0) return [];
  return lease.moveOutData;
}

function allTenantsMovingOut(lease) {
  const moveOuts = getMovingOutTenants(lease);
  if (moveOuts.length === 0) return false;
  const currentTenantIds = new Set(
    (lease.tenants || [])
      .filter((t) => t.status === "Current" || !t.status)
      .map((t) => t.tenantId),
  );
  const movingOutIds = new Set(moveOuts.map((m) => m.tenantId));
  return [...currentTenantIds].every((id) => movingOutIds.has(id));
}

function deduplicateRecurringCharges(charges, leaseView) {
  if (!charges || charges.length === 0) return [];

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const groups = {};
  for (const rt of charges) {
    const glId =
      rt.offsettingGLAccountId ||
      (rt.lines && rt.lines.length > 0 ? rt.lines[0].GLAccountId : null) ||
      "unknown";
    const normalizedMemo = (rt.memo || "").toLowerCase().trim();
    const groupKey = `${glId}:${normalizedMemo}`;
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(rt);
  }

  const result = [];
  for (const key of Object.keys(groups)) {
    const group = groups[key];
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }
    group.sort((a, b) => {
      const dateA = a.firstOccurrenceDate
        ? new Date(a.firstOccurrenceDate).getTime()
        : 0;
      const dateB = b.firstOccurrenceDate
        ? new Date(b.firstOccurrenceDate).getTime()
        : 0;
      return dateA - dateB;
    });
    if (leaseView === "renewed") {
      result.push(group[group.length - 1]);
    } else {
      let current = group[0];
      for (const rt of group) {
        const startDate = rt.firstOccurrenceDate
          ? new Date(rt.firstOccurrenceDate)
          : null;
        if (startDate && startDate <= now) current = rt;
      }
      result.push(current);
    }
  }
  return result;
}

function getLeaseDisplayData(lease, leaseView, formatDateLocal) {
  const increase = lease.property?.yearlyIncrease || 0;
  const currentRent = lease.rent || 0;

  // Exclude account credits — independent balance adjustments, not part
  // of the recurring-charge / rent-increase calculation.
  const allRecurring = (lease.recurringTransactions || []).filter(
    (rt) => !rt.rentId && !rt.isExpired && rt.transactionType !== "Credit",
  );
  const recurringFees = deduplicateRecurringCharges(allRecurring, leaseView);
  const totalRecurringFees = recurringFees.reduce(
    (sum, rt) => sum + (rt.amount || 0),
    0,
  );
  const daysLeft = calculateDaysLeft(lease.leaseToDate);

  let displayedStart = formatDateLocal(lease.leaseFromDate);
  let displayedEnd = formatDateLocal(lease.leaseToDate);
  let displayedRent = currentRent;
  let displayedIncrease = increase > 0 ? currentRent * (increase / 100) : 0;
  let displayedFutureTotal = currentRent + totalRecurringFees;
  let renewalSource = null;
  let renewalIncreasePercent = increase;
  let previousRent = currentRent;
  let isRenewed = false;

  if (leaseView === "renewed") {
    const bHistory = lease.buildiumRenewalHistory || [];
    if (bHistory.length > 0) {
      const latest = bHistory[0];
      displayedRent = latest.rent || lease.latestRenewalRent || currentRent;
      displayedStart = formatDateLocal(
        latest.leaseFromDate || lease.latestRenewalFromDate,
      );
      displayedEnd = formatDateLocal(
        latest.leaseToDate || lease.latestRenewalToDate,
      );
      previousRent = currentRent;
      displayedIncrease = displayedRent - previousRent;
      displayedFutureTotal = displayedRent + totalRecurringFees;
      renewalSource = "buildium";
      renewalIncreasePercent =
        previousRent > 0
          ? Math.round(((displayedRent - previousRent) / previousRent) * 100)
          : 0;
      isRenewed = true;
    } else if (lease.latestRenewalRent) {
      displayedRent = lease.latestRenewalRent;
      displayedStart = formatDateLocal(lease.latestRenewalFromDate);
      displayedEnd = formatDateLocal(lease.latestRenewalToDate);
      previousRent = currentRent;
      displayedIncrease = displayedRent - previousRent;
      displayedFutureTotal = displayedRent + totalRecurringFees;
      renewalSource = "buildium";
      renewalIncreasePercent =
        previousRent > 0
          ? Math.round(((displayedRent - previousRent) / previousRent) * 100)
          : 0;
      isRenewed = true;
    } else if (lease.renewalHistory && lease.renewalHistory.length > 0) {
      const latest = lease.renewalHistory[lease.renewalHistory.length - 1];
      displayedStart = formatDateLocal(latest.newLeaseFromDate);
      displayedEnd = formatDateLocal(latest.newLeaseToDate);
      displayedRent = latest.newRent;
      previousRent = latest.previousRent;
      displayedIncrease = latest.newRent - latest.previousRent;
      displayedFutureTotal = latest.newRent + totalRecurringFees;
      renewalSource = "local";
      renewalIncreasePercent =
        latest.previousRent > 0
          ? Math.round(
              ((latest.newRent - latest.previousRent) / latest.previousRent) *
                100,
            )
          : 0;
      isRenewed = true;
    }
  }

  const newRecurringFees =
    totalRecurringFees +
    (increase > 0 ? totalRecurringFees * (increase / 100) : 0);

  return {
    displayedStart,
    displayedEnd,
    displayedRent,
    displayedIncrease,
    displayedFutureTotal,
    daysLeft,
    recurringFees,
    totalRecurringFees,
    newRecurringFees,
    currentRent,
    previousRent,
    increase,
    renewalSource,
    renewalIncreasePercent,
    isRenewed,
  };
}

function getRenewBlockReason(lease, display) {
  if (!display.increase) return "Set yearly increase on property first";
  if (lease.leaseType !== "FixedWithRollover")
    return "Only FixedWithRollover leases can be renewed";
  if (
    (lease.buildiumRenewalHistory || []).some((r) => r.leaseStatus === "Future")
  )
    return "Already has a future renewal pending";
  if (allTenantsMovingOut(lease))
    return "All tenants have given move-out notice — this lease should not be renewed";
  if (display.daysLeft !== null && display.daysLeft < 90)
    return `Lease expires in ${display.daysLeft} day${display.daysLeft !== 1 ? "s" : ""} — tenant must have 90 days notice. Please extend the lease end date in Buildium first.`;
  return null;
}

// Builds a short list of plain-language facts about this lease's renewal
// eligibility/status — shown in the info popover regardless of whether the
// Renew button is blocked, since context is useful either way.
function getLeaseInfoDetails(lease, display, blockReason) {
  const details = [];

  details.push(
    `Lease type: ${lease.leaseType || "Unknown"}${
      lease.leaseType === "FixedWithRollover"
        ? " (renewable)"
        : " (not renewable here)"
    }`,
  );

  if (display.increase > 0) {
    details.push(`Yearly increase configured: ${display.increase}%`);
  } else {
    details.push("No yearly increase set on this property yet");
  }

  if (display.daysLeft !== null) {
    details.push(
      display.daysLeft <= 0
        ? `Lease expired ${Math.abs(display.daysLeft)} day${Math.abs(display.daysLeft) !== 1 ? "s" : ""} ago`
        : `${display.daysLeft} day${display.daysLeft !== 1 ? "s" : ""} until lease end (90 required to renew)`,
    );
  }

  const moveOuts = getMovingOutTenants(lease);
  if (moveOuts.length > 0) {
    details.push(
      allTenantsMovingOut(lease)
        ? "All current tenants have given move-out notice"
        : `${moveOuts.length} tenant${moveOuts.length !== 1 ? "s" : ""} on this lease ${moveOuts.length !== 1 ? "have" : "has"} given move-out notice`,
    );
  }

  const futureRenewal = (lease.buildiumRenewalHistory || []).find(
    (r) => r.leaseStatus === "Future",
  );
  if (futureRenewal) {
    details.push(
      `Future renewal already on file, starting ${
        futureRenewal.leaseFromDate
          ? new Date(futureRenewal.leaseFromDate).toLocaleDateString()
          : "an unknown date"
      }`,
    );
  }

  if (blockReason) {
    details.push(`Renew is disabled: ${blockReason}`);
  } else {
    details.push("Renew is available for this lease");
  }

  return details;
}

const DEFAULT_FILTERS = {
  propertyId: "",
  status: "Active",
  type: "",
  expiry: [],
  leaseFrom: "",
  leaseTo: "",
  createdAfter: "",
  search: "",
  page: 1,
  limit: 20,
};

export default function BuildiumLeases() {
  const [leases, setLeases] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [leaseView, setLeaseView] = useState("current");
  const [expiryDropdownOpen, setExpiryDropdownOpen] = useState(false);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  // Single modal state — lease + which tab to open on
  const [modalState, setModalState] = useState({ lease: null, tab: "renewal" });

  const [syncLockedUntil, setSyncLockedUntil] = useState(() => {
    const stored = localStorage.getItem("leaseSyncLockedUntil");
    return stored ? Number(stored) : null;
  });
  const isSyncLocked = syncLockedUntil && Date.now() < syncLockedUntil;

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Search box has its own local state so typing feels instant; the debounced
  // value is what actually drives the fetch.
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceRef = useRef(null);

  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    confirmText: "OK",
    cancelText: null,
    onConfirm: () => {},
  });

  const [exportModalOpen, setExportModalOpen] = useState(false);

  const showAlert = (
    type,
    title,
    message,
    confirmText = "OK",
    cancelText = null,
    onConfirm = () => {},
  ) => {
    setAlert({
      open: true,
      type,
      title,
      message,
      confirmText,
      cancelText,
      onConfirm,
    });
  };

  const closeAlert = () => setAlert((prev) => ({ ...prev, open: false }));

  const openRenewalModal = (lease) => setModalState({ lease, tab: "renewal" });
  const openN1Modal = (lease) => setModalState({ lease, tab: "n1form" });
  const closeModal = () => setModalState({ lease: null, tab: "renewal" });

  // Close the expiry dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".expiry-dropdown-container"))
        setExpiryDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(`${API_BASE}/buildium/properties`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProperties(res.data || []);
      } catch (err) {
        console.error("Failed to load properties for filter", err);
      }
    };
    fetchProperties();
  }, []);

  // Debounce free-text search: update filters.search only after the user
  // pauses typing, so we don't refetch on every keystroke.
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) return prev;
        return { ...prev, search: searchInput, page: 1 };
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput]);

  useEffect(() => {
    fetchLeases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, leaseView]);

  const fetchLeases = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");

    try {
      const params = {
        page: filters.page,
        limit: filters.limit,
        renewed: leaseView === "renewed",
      };
      if (filters.propertyId) params.propertyId = filters.propertyId;
      if (filters.status) params.status = filters.status;
      if (filters.type) params.type = filters.type;
      if (filters.search) params.search = filters.search;
      if (filters.leaseFrom) params.leaseFromDate = filters.leaseFrom;
      if (filters.leaseTo) params.leaseToDate = filters.leaseTo;
      if (filters.createdAfter) params.createdAfter = filters.createdAfter;
      if (filters.expiry.length > 0) params.expiry = filters.expiry;

      const res = await axios.get(`${API_BASE}/buildium/leases/v2`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLeases(res.data.leases || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch leases:", err);
      setError("Could not load leases. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const syncLeases = async () => {
    if (syncing || isSyncLocked) return;
    setSyncing(true);
    setError(null);
    const token = localStorage.getItem("token");

    try {
      const res = await axios.post(
        `${API_BASE}/buildium/leases/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data.success) {
        const lockedUntil = Date.now() + SYNC_COOLDOWN_MS;
        setSyncLockedUntil(lockedUntil);
        localStorage.setItem("leaseSyncLockedUntil", String(lockedUntil));

        showAlert(
          "success",
          "Sync Started",
          `Syncing ${res.data.syncing} active leases from Buildium. The page will refresh automatically in 5 minutes.`,
          "OK",
        );

        setTimeout(() => {
          setSyncLockedUntil(null);
          localStorage.removeItem("leaseSyncLockedUntil");
          fetchLeases();
        }, SYNC_COOLDOWN_MS);
      }
    } catch (err) {
      console.error("Lease sync failed:", err);
      showAlert(
        "error",
        "Sync Failed",
        err.response?.data?.message || "Failed to sync leases.",
        "OK",
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const toggleExpiryFilter = (value) => {
    setFilters((prev) => {
      const current = prev.expiry || [];
      const newExpiry = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, expiry: newExpiry, page: 1 };
    });
  };

  const removeExpiryFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      expiry: prev.expiry.filter((v) => v !== value),
      page: 1,
    }));
  };

  const formatDateLocal = (dateStr) => {
    if (!dateStr) return "—";
    const [year, month, day] = dateStr.split("T")[0].split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const clearFilters = () => {
    setSearchInput("");
    setFilters(DEFAULT_FILTERS);
  };

  const propertyName = (id) => properties.find((p) => p._id === id)?.name;
  const expiryLabel = (value) =>
    EXPIRY_OPTIONS.find((o) => o.value === value)?.label || value;
  const typeLabel = (value) =>
    LEASE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value;

  // ── Active filter chips (excludes status="Active" since that's the default) ──
  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.propertyId) {
      chips.push({
        key: "propertyId",
        label: `Property: ${propertyName(filters.propertyId) || "Unknown"}`,
        onRemove: () => handleFilterChange("propertyId", ""),
      });
    }
    if (filters.status !== "Active") {
      chips.push({
        key: "status",
        label: `Status: ${filters.status || "All"}`,
        onRemove: () => handleFilterChange("status", "Active"),
      });
    }
    if (filters.type) {
      chips.push({
        key: "type",
        label: `Type: ${typeLabel(filters.type)}`,
        onRemove: () => handleFilterChange("type", ""),
      });
    }
    filters.expiry.forEach((val) => {
      chips.push({
        key: `expiry-${val}`,
        label: `Expiry: ${expiryLabel(val)}`,
        onRemove: () => removeExpiryFilter(val),
      });
    });
    if (filters.leaseFrom) {
      chips.push({
        key: "leaseFrom",
        label: `Start ≥ ${filters.leaseFrom}`,
        onRemove: () => handleFilterChange("leaseFrom", ""),
      });
    }
    if (filters.leaseTo) {
      chips.push({
        key: "leaseTo",
        label: `End ≤ ${filters.leaseTo}`,
        onRemove: () => handleFilterChange("leaseTo", ""),
      });
    }
    if (filters.createdAfter) {
      chips.push({
        key: "createdAfter",
        label: `Created after ${filters.createdAfter}`,
        onRemove: () => handleFilterChange("createdAfter", ""),
      });
    }
    if (filters.search) {
      chips.push({
        key: "search",
        label: `Search: "${filters.search}"`,
        onRemove: () => {
          setSearchInput("");
          handleFilterChange("search", "");
        },
      });
    }
    return chips;
  }, [filters, properties]);

  const hasActiveFilters = activeChips.length > 0;
  const totalPages = Math.ceil(total / filters.limit);

  // Defense in depth — the server already excludes untracked "Fixed" leases
  // and applies the expiry-bucket filter, so this is just a safety net.
  const visibleLeases = useMemo(() => {
    return leases.filter(
      (lease) => !EXCLUDED_LEASE_TYPES.includes(lease.leaseType),
    );
  }, [leases]);

  if (loading && leases.length === 0) {
    return <div className="p-8 text-center">Loading leases...</div>;
  }

  return (
    <div className="p-1 mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Buildium Leases</h2>
        <div className="flex items-center gap-3">
          {/* <button
            onClick={syncLeases}
            disabled={syncing || isSyncLocked}
            title={
              isSyncLocked
                ? "A sync was started recently. Try again in a few minutes."
                : "Pull the latest leases, tenants and move-outs from Buildium"
            }
            className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-white ${
              syncing || isSyncLocked
                ? "bg-blue-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            <FiRefreshCw
              className={syncing || isSyncLocked ? "animate-spin" : ""}
            />
            {syncing
              ? "Starting..."
              : isSyncLocked
                ? "Sync in progress"
                : "Sync from Buildium"}
          </button> */}

          {leaseView === "renewed" && (
            <button
              onClick={() => setExportModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium bg-emerald-600 hover:bg-emerald-700"
            >
              <FiDownload />
              Export Report
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <div className="flex">
          {["current", "renewed"].map((view) => (
            <button
              key={view}
              onClick={() => {
                setLeaseView(view);
                setFilters((p) => ({ ...p, page: 1 }));
              }}
              className={`px-8 py-4 font-semibold text-sm border-b-2 transition-all ${
                leaseView === view
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {view === "current" ? "Current Leases" : "Renewed Leases"}
              {view === "renewed" && leaseView === "renewed" && total > 0 && (
                <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                  {total}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="mb-4 bg-white rounded-lg shadow border border-gray-200">
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by tenant name or unit number..."
              className="w-full border border-gray-300 rounded-lg py-2.5 px-4 pl-10 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="p-4 flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">
              Property
            </label>
            <select
              value={filters.propertyId}
              onChange={(e) => handleFilterChange("propertyId", e.target.value)}
              className="border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition min-w-[180px]"
            >
              <option value="">All Properties</option>
              {properties.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Status</label>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              {["", "Active", "Expired"].map((s) => (
                <button
                  key={s}
                  onClick={() => handleFilterChange("status", s)}
                  className={`px-3 py-2 text-sm font-medium transition ${
                    filters.status === s
                      ? "bg-blue-500 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {s || "All"}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">
              Lease Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange("type", e.target.value)}
              className="border border-gray-300 rounded-lg py-2 px-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition min-w-[170px]"
            >
              {LEASE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 expiry-dropdown-container relative">
            <label className="text-xs font-medium text-gray-500">
              Expires In
            </label>
            <button
              onClick={() => setExpiryDropdownOpen(!expiryDropdownOpen)}
              className={`border rounded-lg py-2 px-3 text-sm bg-white transition min-w-[200px] text-left flex items-center justify-between gap-2 ${
                filters.expiry.length > 0
                  ? "border-blue-400 ring-1 ring-blue-200"
                  : "border-gray-300"
              }`}
            >
              <span className="truncate">
                {filters.expiry.length === 0
                  ? "Any time"
                  : `${filters.expiry.length} range${filters.expiry.length !== 1 ? "s" : ""} selected`}
              </span>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${expiryDropdownOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {expiryDropdownOpen && (
              <div className="absolute z-20 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                <div className="px-3 py-1.5 text-xs text-gray-400 border-b border-gray-100">
                  Select any number of ranges — matches are combined
                </div>
                {EXPIRY_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filters.expiry.includes(opt.value)}
                      onChange={() => toggleExpiryFilter(opt.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                    />
                    {opt.label}
                  </label>
                ))}
                {filters.expiry.length > 0 && (
                  <button
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, expiry: [], page: 1 }))
                    }
                    className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 border-t border-gray-100"
                  >
                    Clear expiry selection
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500 opacity-0">
              More
            </label>
            <button
              onClick={() => setMoreFiltersOpen((prev) => !prev)}
              className={`inline-flex items-center gap-1.5 border rounded-lg py-2 px-3 text-sm font-medium transition ${
                moreFiltersOpen ||
                filters.leaseFrom ||
                filters.leaseTo ||
                filters.createdAfter
                  ? "border-blue-400 bg-blue-50 text-blue-700"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FiFilter className="w-3.5 h-3.5" />
              More Filters
              {(filters.leaseFrom ||
                filters.leaseTo ||
                filters.createdAfter) && (
                <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-blue-600 text-white rounded-full">
                  {
                    [
                      filters.leaseFrom,
                      filters.leaseTo,
                      filters.createdAfter,
                    ].filter(Boolean).length
                  }
                </span>
              )}
            </button>
          </div>

          <div className="flex-1" />

          {hasActiveFilters && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 opacity-0">
                Clear
              </label>
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium flex items-center gap-1.5 transition border border-transparent"
              >
                <FiX className="w-3.5 h-3.5" /> Clear All Filters
              </button>
            </div>
          )}
        </div>

        {moreFiltersOpen && (
          <div className="px-4 pb-4 pt-1 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "Lease Start On or After", key: "leaseFrom" },
              { label: "Lease End On or Before", key: "leaseTo" },
              { label: "Created After", key: "createdAfter" },
            ].map(({ label, key }) => (
              <div key={key}>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {label}
                </label>
                <input
                  type="date"
                  value={filters[key]}
                  onChange={(e) => handleFilterChange(key, e.target.value)}
                  className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Active filter chips ── */}
      {hasActiveFilters && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">
            Active filters:
          </span>
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium rounded-full"
            >
              {chip.label}
              <button
                onClick={chip.onRemove}
                className="hover:bg-blue-200 rounded-full p-0.5 transition"
                title="Remove filter"
              >
                <FiX className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button
            onClick={clearFilters}
            className="text-xs text-gray-400 hover:text-red-600 font-medium underline ml-1"
          >
            Clear all
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <h3 className="text-lg font-semibold text-gray-800">
          {leaseView === "current" ? "Active Leases" : "Renewed Leases"}
        </h3>
        {leaseView === "renewed" && (
          <p className="text-sm text-gray-500">
            Leases confirmed renewed in Buildium (future rent schedule detected)
          </p>
        )}
        <span className="text-sm text-gray-400">{total} total</span>
      </div>

      <div className="bg-white shadow-md rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unit
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Property
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tenants
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {leaseView === "renewed" ? "Renewal Start" : "Start"}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {leaseView === "renewed" ? "Renewal End" : "End"}
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {leaseView === "renewed" ? "Renewed Rent" : "Rent"}
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Recurring Fees
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {leaseView === "renewed" ? "Renewal Increase" : "Increase"}
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {leaseView === "renewed" ? "Renewed Total" : "Future Rent"}
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Days Left
              </th>
              {leaseView === "renewed" && (
                <>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Renewals
                  </th>
                </>
              )}
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {visibleLeases.length === 0 ? (
              <tr>
                <td
                  colSpan={leaseView === "renewed" ? 14 : 12}
                  className="px-6 py-12 text-center"
                >
                  {hasActiveFilters ? (
                    <div className="flex flex-col items-center gap-3">
                      <div className="text-gray-500">
                        No leases match your current filters.
                      </div>
                      <div className="text-sm text-gray-400 max-w-md">
                        Try removing a filter below, or clear them all to see
                        every lease.
                      </div>
                      <button
                        onClick={clearFilters}
                        className="mt-1 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg border border-blue-200"
                      >
                        Clear all filters
                      </button>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      {leaseView === "renewed"
                        ? "No renewed leases found. Renewals are detected from Buildium rent schedules during sync."
                        : "No leases found. Try syncing with Buildium."}
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              visibleLeases.map((lease) => {
                const display = getLeaseDisplayData(
                  lease,
                  leaseView,
                  formatDateLocal,
                );
                const moveOuts = getMovingOutTenants(lease);
                const everyoneMovingOut = allTenantsMovingOut(lease);
                const blockReason = getRenewBlockReason(lease, display);
                const isBlocked = !!blockReason;
                const isAlreadyRenewed = (
                  lease.buildiumRenewalHistory || []
                ).some((r) => r.leaseStatus === "Future");

                const tenantDisplay = (lease.tenants || []).map((t) => {
                  const moveOut = moveOuts.find(
                    (m) => m.tenantId === t.tenantId,
                  );
                  return { ...t, moveOut };
                });

                return (
                  <tr
                    key={lease._id}
                    className={`hover:bg-gray-50 ${everyoneMovingOut ? "bg-red-50/30" : ""}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {lease.unitNumber || "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lease.property?.name || "Unknown"}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="space-y-1">
                        {tenantDisplay.length > 0 ? (
                          tenantDisplay.map((t, idx) => (
                            <div
                              key={t.tenantId || idx}
                              className="flex items-center gap-1.5 flex-wrap"
                            >
                              <span>
                                {t.firstName || ""} {t.lastName || ""}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span>—</span>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {display.displayedStart}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {display.displayedEnd}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                      <div className="font-medium">
                        ${display.displayedRent.toFixed(2)}
                      </div>
                      {leaseView === "current" && display.increase > 0 && (
                        <div className="text-xs text-green-600">
                          → $
                          {(
                            display.currentRent +
                            display.currentRent * (display.increase / 100)
                          ).toFixed(2)}
                        </div>
                      )}
                      {leaseView === "renewed" &&
                        display.previousRent !== display.displayedRent && (
                          <div className="text-xs text-gray-400">
                            was ${display.previousRent.toFixed(2)}
                          </div>
                        )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                      {display.recurringFees.length > 0 ? (
                        <>
                          <div className="font-medium">
                            ${display.totalRecurringFees.toFixed(2)}
                          </div>
                          {leaseView === "current" && display.increase > 0 && (
                            <div className="text-xs text-green-600">
                              → ${display.newRecurringFees.toFixed(2)}
                            </div>
                          )}
                          <div className="text-xs text-gray-400">
                            {display.recurringFees.length} charge
                            {display.recurringFees.length !== 1 ? "s" : ""}
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                      {display.displayedIncrease > 0 ? (
                        <>
                          <div className="font-medium text-blue-600">
                            ${display.displayedIncrease.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">
                            ({display.renewalIncreasePercent}%)
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                      <div className="font-semibold text-green-700">
                        ${display.displayedFutureTotal.toFixed(2)}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            lease.leaseStatus === "Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {lease.leaseStatus || "Unknown"}
                        </span>
                        {everyoneMovingOut ? (
                          <span className="mt-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                              />
                            </svg>
                            All Vacating
                          </span>
                        ) : moveOuts.length > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                              />
                            </svg>
                            {moveOuts.length} Moving Out
                          </span>
                        ) : null}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                      {display.daysLeft === null ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            display.daysLeft <= 0
                              ? "text-red-700 bg-red-100"
                              : display.daysLeft < 90
                                ? "text-red-600 bg-red-50"
                                : display.daysLeft <= 180
                                  ? "text-yellow-700 bg-yellow-50"
                                  : "text-green-600 bg-green-50"
                          }`}
                        >
                          {display.daysLeft <= 0
                            ? `Expired (${Math.abs(display.daysLeft)}d ago)`
                            : `${display.daysLeft}d`}
                        </span>
                      )}
                    </td>

                    {leaseView === "renewed" && (
                      <>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              display.renewalSource === "buildium"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {display.renewalSource === "buildium"
                              ? "Buildium"
                              : "Local"}
                          </span>
                          {lease.renewalAbsorbed && (
                            <div className="text-xs text-green-600 mt-1 font-medium">
                              Active
                            </div>
                          )}
                          {!lease.renewalAbsorbed &&
                            lease.latestRenewalFromDate && (
                              <div className="text-xs text-gray-400 mt-1">
                                Starts{" "}
                                {formatDateLocal(lease.latestRenewalFromDate)}
                              </div>
                            )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="font-medium text-amber-600">
                            Renewed {lease.renewalHistory?.length || 0}x
                          </div>
                          {lease.rentScheduleCount > 1 && (
                            <div className="text-xs text-gray-400">
                              {lease.rentScheduleCount} rent schedule
                              {lease.rentScheduleCount !== 1 ? "s" : ""}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            Last:{" "}
                            {lease.renewalHistory?.length > 0
                              ? lease.renewalHistory[
                                  lease.renewalHistory.length - 1
                                ]?.renewedAt
                                ? new Date(
                                    lease.renewalHistory[
                                      lease.renewalHistory.length - 1
                                    ].renewedAt,
                                  ).toLocaleDateString()
                                : "—"
                              : lease.lastSyncedAt
                                ? new Date(
                                    lease.lastSyncedAt,
                                  ).toLocaleDateString()
                                : "—"}
                          </div>
                        </td>
                      </>
                    )}

                    {/* ── Actions column ── */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        {/* Renew button — current view only, or renewed view for re-renew */}
                        <button
                          onClick={() => !isBlocked && openRenewalModal(lease)}
                          disabled={isBlocked}
                          title={
                            blockReason || "Generate Lease Renewal in Buildium"
                          }
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                            isBlocked
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                          }`}
                        >
                          <FiRefreshCw className="w-3.5 h-3.5" />
                          {isAlreadyRenewed
                            ? "Renewed"
                            : everyoneMovingOut
                              ? "Vacating"
                              : "Renew"}
                        </button>

                        <LeaseInfoPopover
                          details={getLeaseInfoDetails(
                            lease,
                            display,
                            blockReason,
                          )}
                          isBlocked={isBlocked}
                        />

                        {/* N1 button — only on the renewed view */}
                        {leaseView === "renewed" && (
                          <button
                            onClick={() => openN1Modal(lease)}
                            title="Preview / Download N1 PDF"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200"
                          >
                            <FiFileText className="w-3.5 h-3.5" />
                            N1
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-gray-700">
            Showing {(filters.page - 1) * filters.limit + 1} to{" "}
            {Math.min(filters.page * filters.limit, total)} of {total} leases
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 1}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page >= totalPages}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 text-sm text-gray-500 text-center">
        Last updated: {new Date().toLocaleString()}
      </div>

      <ExportRenewalModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
      />

      {/* Single RenewalModal instance — tab controlled by modalState */}
      <RenewalModal
        open={!!modalState.lease}
        onClose={closeModal}
        lease={modalState.lease}
        initialTab={modalState.tab}
        onRenewalSuccess={() => fetchLeases()}
        guidelineLimit={ONTARIO_GUIDELINE_LIMIT}
      />

      <AlertDialog
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText={alert.confirmText}
        cancelText={alert.cancelText}
        open={alert.open}
        onClose={closeAlert}
        onConfirm={() => {
          alert.onConfirm();
          closeAlert();
        }}
      />
    </div>
  );
}
